// FLAGS: none
// Sits above the Suspense boundary in LazyApp.tsx, outside the lazy App chunk, so a chunk that fails to load
// (typically a 404 after a deploy replaced hashed assets under a cached index.html) tears down the static boot
// loader, is reported, and offers a reload instead of spinning for ever. A class because React only lets
// class components catch render errors.
import { Component, ReactNode } from "react";
import { failBoot } from "@src/boot/bootLoader";
import i18n from "@src/i18n";

interface BootErrorBoundaryProps {
  children: ReactNode;
}

interface BootErrorBoundaryState {
  failed: boolean;
}

class BootErrorBoundary extends Component<
  BootErrorBoundaryProps,
  BootErrorBoundaryState
> {
  constructor(props: BootErrorBoundaryProps) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError(): BootErrorBoundaryState {
    return { failed: true };
  }

  componentDidCatch(error: Error): void {
    failBoot(error);
  }

  render(): ReactNode {
    const { failed } = this.state;
    const { children } = this.props;
    if (!failed) {
      return children;
    }
    return (
      <div
        role="alert"
        className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center"
      >
        <p>{i18n.t("ErrorStrings.NetworkError")}</p>
        <button
          type="button"
          className="rounded-full border px-6 py-2"
          onClick={() => window.location.reload()}
        >
          {i18n.t("SharedStrings.Retry")}
        </button>
      </div>
    );
  }
}

export default BootErrorBoundary;
